import React, { useState, useEffect } from "react";
import {
  FaMicrophone,
  FaDollarSign,
  FaVideo,
  FaHistory,
  FaHourglassHalf,
  FaCheckCircle,
  FaTimes,
  FaUsers,
  FaTrophy,
  FaArrowRight,
  FaBolt,
  FaWallet,
  FaClipboardList,
  FaChevronRight,
  FaPlay,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const PressDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [freeStats, setFreeStats] = useState({ pending: 0, approved: 0, rejected: 0, completed: 0, total: 0 });
  const [paidStats, setPaidStats] = useState({ pending: 0, approved: 0, rejected: 0, completed: 0, total: 0 });

  const pressUser = (() => {
    try { return JSON.parse(localStorage.getItem("pressUser")) || {}; } catch { return {}; }
  })();
  const userName = pressUser?.name || pressUser?.fullName || "Press Member";

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem("pressToken")}` };

        const [freeRes, paidRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/press/free-conference/user-conferences`, { headers }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/user-conferences`, { headers }),
        ]);

        if (freeRes.data.success) {
          const d = freeRes.data.data;
          setFreeStats({
            pending: d.filter(c => c.status === "pending").length,
            approved: d.filter(c => c.status === "approved").length,
            rejected: d.filter(c => c.status === "rejected").length,
            completed: d.filter(c => c.status === "completed").length,
            total: d.length,
          });
        }
        if (paidRes.data.success) {
          const d = paidRes.data.data;
          setPaidStats({
            pending: d.filter(c => c.status === "pending").length,
            approved: d.filter(c => c.status === "approved").length,
            rejected: d.filter(c => c.status === "rejected").length,
            completed: d.filter(c => c.status === "completed").length,
            total: d.length,
          });
        }
      } catch (e) {
        console.error("Error fetching stats:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalAll = freeStats.total + paidStats.total;
  const totalActive = freeStats.approved + paidStats.approved;
  const totalPending = freeStats.pending + paidStats.pending;
  const totalCompleted = freeStats.completed + paidStats.completed;

  const statCards = [
    { label: "Total", value: totalAll, icon: FaClipboardList, color: "bg-indigo-500", light: "bg-indigo-50 text-indigo-600" },
    { label: "Active", value: totalActive, icon: FaBolt, color: "bg-emerald-500", light: "bg-emerald-50 text-emerald-600" },
    { label: "Pending", value: totalPending, icon: FaHourglassHalf, color: "bg-amber-500", light: "bg-amber-50 text-amber-600" },
    { label: "Done", value: totalCompleted, icon: FaCheckCircle, color: "bg-blue-500", light: "bg-blue-50 text-blue-600" },
  ];

  const quickActions = [
    {
      title: "Free Conference",
      desc: "Create at no cost",
      icon: FaMicrophone,
      from: "from-emerald-500",
      to: "to-green-600",
      shadow: "shadow-emerald-200",
      path: "/press/free-conference",
      badge: freeStats.total,
    },
    {
      title: "Paid Conference",
      desc: "Book premium slots",
      icon: FaDollarSign,
      from: "from-amber-500",
      to: "to-yellow-600",
      shadow: "shadow-amber-200",
      path: "/press/paid-conference",
      badge: paidStats.total,
    },
    {
      title: "Free Status",
      desc: "Track free events",
      icon: FaVideo,
      from: "from-purple-500",
      to: "to-violet-600",
      shadow: "shadow-purple-200",
      path: "/press/conference-status",
      badge: freeStats.approved + freeStats.completed,
    },
    {
      title: "Paid Status",
      desc: "Track paid events",
      icon: FaTrophy,
      from: "from-blue-500",
      to: "to-indigo-600",
      shadow: "shadow-blue-200",
      path: "/press/paid-conference-status",
      badge: paidStats.approved + paidStats.completed,
    },
    {
      title: "Wallet",
      desc: "Manage funds",
      icon: FaWallet,
      from: "from-teal-500",
      to: "to-cyan-600",
      shadow: "shadow-teal-200",
      path: "/press/wallet",
      badge: null,
    },
    {
      title: "Payments",
      desc: "View history",
      icon: FaHistory,
      from: "from-rose-500",
      to: "to-pink-600",
      shadow: "shadow-rose-200",
      path: "/press/history-payment",
      badge: null,
    },
  ];

  const SkeletonStat = () => (
    <div className="bg-white rounded-2xl p-3 animate-pulse flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-slate-200 flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 bg-slate-200 rounded w-12" />
        <div className="h-5 bg-slate-200 rounded w-8" />
      </div>
    </div>
  );

  const SkeletonAction = () => (
    <div className="bg-white rounded-2xl p-4 animate-pulse space-y-3">
      <div className="w-10 h-10 rounded-xl bg-slate-200" />
      <div className="h-4 bg-slate-200 rounded w-3/4" />
      <div className="h-3 bg-slate-200 rounded w-1/2" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-24">

      {/* ── Hero Header ── */}
      <div className="relative bg-gradient-to-br from-indigo-700 via-blue-700 to-blue-800 rounded-[1.5rem] p-5 sm:p-8 mb-4 overflow-hidden">
        {/* decorative circles */}
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute bottom-0 left-1/2 w-56 h-56 rounded-full bg-white/5 -translate-x-1/4 translate-y-1/2" />

        <div className="relative z-10 flex items-start justify-between gap-3">
          <div>
            <p className="text-white/60 text-[11px] font-bold uppercase tracking-widest mb-0.5">Welcome back</p>
            <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-tight">
              {userName} 👋
            </h1>
            <p className="text-white/70 text-xs sm:text-sm mt-1 font-medium">Press Conference Dashboard</p>
          </div>
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
            <FaMicrophone className="text-white w-5 h-5" />
          </div>
        </div>

        {/* overall summary pills */}
        <div className="relative z-10 flex gap-2 mt-5 flex-wrap">
          <div className="px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-white text-[10px] font-bold border border-white/20">
            🎙 {freeStats.total} Free
          </div>
          <div className="px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-white text-[10px] font-bold border border-white/20">
            💰 {paidStats.total} Paid
          </div>
          <div className="px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-white text-[10px] font-bold border border-white/20">
            ✅ {totalCompleted} Completed
          </div>
        </div>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        {loading
          ? [0, 1, 2, 3].map(i => <SkeletonStat key={i} />)
          : statCards.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-3 sm:p-4 flex items-center gap-3 shadow-sm border border-slate-50">
              <div className={`w-10 h-10 rounded-xl ${s.light} flex items-center justify-center flex-shrink-0`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">{s.label}</p>
                <p className="text-2xl font-black text-slate-800 leading-none mt-0.5">{s.value}</p>
              </div>
            </div>
          ))
        }
      </div>

      {/* ── Quick Actions ── */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-black text-slate-700 uppercase tracking-widest">Quick Actions</h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {loading
            ? [0, 1, 2, 3, 4, 5].map(i => <SkeletonAction key={i} />)
            : quickActions.map((a, i) => (
              <button
                key={i}
                onClick={() => navigate(a.path)}
                className={`relative group bg-gradient-to-br ${a.from} ${a.to} rounded-2xl p-4 text-left overflow-hidden shadow-lg ${a.shadow} shadow-md active:scale-95 transition-all duration-200`}
              >
                {/* Background decoration */}
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/10 rounded-full" />

                {/* Badge */}
                {a.badge !== null && (
                  <div className="absolute top-2.5 right-2.5 bg-white/20 backdrop-blur-sm text-white text-[10px] font-black px-1.5 py-0.5 rounded-full border border-white/30 min-w-[20px] text-center">
                    {a.badge}
                  </div>
                )}

                <div className="relative z-10">
                  <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
                    <a.icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-white font-black text-sm leading-tight">{a.title}</p>
                  <p className="text-white/70 text-[10px] font-medium mt-0.5 leading-tight">{a.desc}</p>
                  <FaArrowRight className="mt-2 text-white/50 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            ))
          }
        </div>
      </div>

      {/* ── Breakdown Section ── */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {/* Free breakdown */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <FaMicrophone className="w-3 h-3 text-emerald-600" />
                </div>
                <span className="text-xs font-black text-slate-700 uppercase tracking-wide">Free Conferences</span>
              </div>
              <button onClick={() => navigate("/press/conference-status")} className="text-[10px] text-blue-500 font-bold flex items-center gap-0.5">
                View <FaChevronRight className="w-2 h-2" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Pending", val: freeStats.pending, color: "bg-amber-50 text-amber-700" },
                { label: "Running", val: freeStats.approved, color: "bg-blue-50 text-blue-700" },
                { label: "Completed", val: freeStats.completed, color: "bg-emerald-50 text-emerald-700" },
                { label: "Rejected", val: freeStats.rejected, color: "bg-red-50 text-red-700" },
              ].map((s, i) => (
                <div key={i} className={`${s.color} rounded-xl px-2.5 py-1.5 flex justify-between items-center`}>
                  <span className="text-[10px] font-bold">{s.label}</span>
                  <span className="text-sm font-black">{s.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Paid breakdown */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                  <FaDollarSign className="w-3 h-3 text-amber-600" />
                </div>
                <span className="text-xs font-black text-slate-700 uppercase tracking-wide">Paid Conferences</span>
              </div>
              <button onClick={() => navigate("/press/paid-conference-status")} className="text-[10px] text-blue-500 font-bold flex items-center gap-0.5">
                View <FaChevronRight className="w-2 h-2" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Pending", val: paidStats.pending, color: "bg-amber-50 text-amber-700" },
                { label: "Running", val: paidStats.approved, color: "bg-blue-50 text-blue-700" },
                { label: "Completed", val: paidStats.completed, color: "bg-emerald-50 text-emerald-700" },
                { label: "Rejected", val: paidStats.rejected, color: "bg-red-50 text-red-700" },
              ].map((s, i) => (
                <div key={i} className={`${s.color} rounded-xl px-2.5 py-1.5 flex justify-between items-center`}>
                  <span className="text-[10px] font-bold">{s.label}</span>
                  <span className="text-sm font-black">{s.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Video Guide Banner – Bottom ── */}
      <div className="mt-6 bg-gradient-to-br from-red-600 via-rose-600 to-red-700 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
            <span className="bg-white/20 text-white text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest animate-pulse flex items-center gap-1.5">
              <FaPlay className="text-[8px] ml-0.5" /> Video Guide
            </span>
            <span className="text-red-100 text-xs font-bold tracking-wide">Watch &amp; Learn</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-[1000] tracking-tight mb-2 uppercase">How to use Press Conference</h2>
          <p className="text-xs sm:text-sm text-red-100 leading-relaxed max-w-md opacity-90">
            Watch this quick tutorial to learn how to book a free or paid press conference, track your status, and manage your wallet.
          </p>
        </div>

        {/* Embedded YouTube Video Player */}
        <div className="w-full max-w-[200px] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 shrink-0 transition-transform duration-700 hover:scale-105">
          <iframe
            src="https://www.youtube.com/embed/RO4aen4-p0M"
            title="How to use Press Conference"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </div>
      </div>

    </div>
  );
};

export default PressDashboard;
